import { useAuthenticator } from "@aws-amplify/ui-react";
import { useStore } from "@nanostores/react";
import { redirectPage } from "@nanostores/router";
import * as Sentry from "@sentry/react";
import { fetchAuthSession } from "aws-amplify/auth";
import { lazy, ReactElement, Suspense, useEffect } from "react";

import { client } from "../api/client.gen.ts";
import { WorkflowStatus } from "../api/types.gen.ts";
import { StackedLayout } from "../components/catalyst/StackedLayout.tsx";
import { GraphLayout } from "../components/GraphLayout.tsx";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { RailLayout } from "../components/RailLayout.tsx";
import { getAppConfig } from "../config/get-app-config.ts";
import { withDataLoaded } from "../context/withDataLoaded.tsx";
import { $alertSourceContent } from "../stores/alert-source.ts";
import { $destinationContent } from "../stores/destinations.ts";
import { $entityContent } from "../stores/entities.ts";
import { $entityClass } from "../stores/entity-classes.ts";
import { $eventContent } from "../stores/events.ts";
import { $integrationContent } from "../stores/integrations.ts";
import { $knowledgeBaseContent } from "../stores/knowledge-base.ts";
import { $queryActionContent } from "../stores/queryactions.ts";
import { $router } from "../stores/router.ts";
import { AuthCallback } from "./AuthCallback.tsx";
import { PageNotFound } from "./PageNotFound.tsx";
import SignIn from "./SignIn.tsx";

const HomePage = lazy(() => import("./Home.tsx"));
const IntegrationsList = lazy(() => import("./IntegrationsList.tsx"));
const IntegrationType = lazy(() => import("./IntegrationType.tsx"));
const SettingsPage = lazy(() => import("./Settings.tsx"));
const Graph = lazy(() => import("./Graph.tsx"));
const IntegrationForm = lazy(() => import("./IntegrationForm.tsx"));
const EntitiesPage = lazy(() => import("./Entities.tsx"));
const EntityClassFormPage = lazy(() => import("./EntityClassFormPage.tsx"));
const EntityFormFullPage = lazy(() => import("./EntityFormFullPage.tsx"));
const QueryActionForm = lazy(() => import("./QueryActionForm.tsx"));
const Dashboard = lazy(() => import("./Dashboard.tsx"));
const Alerts = lazy(() => import("./Alerts.tsx"));
const AlertDetails = lazy(() => import("./AlertDetails.tsx"));
const AlertSourceForm = lazy(() => import("./AlertSourceForm.tsx"));
const AlertSourcesList = lazy(() => import("./AlertSourcesList.tsx"));
const KnowledgeBase = lazy(() => import("./KnowledgeBase.tsx"));
const KnowledgeBaseDetail = lazy(() => import("./KnowledgeBaseDetail.tsx"));
const Destinations = lazy(() => import("./Destinations.tsx"));
const DestinationType = lazy(() => import("./DestinationType.tsx"));
const DestinationForm = lazy(() => import("./DestinationForm.tsx"));
const Chat = lazy(() => import("./Chat.tsx"));
const InvokeAgent = lazy(() => import("./InvokeAgent.tsx"));

// Loader wrappers for HOC-safe usage
const IntegrationEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($integrationContent, IntegrationForm, {
    propKey: "integration",
  });
  return <Wrapped />;
};

const QueryActionNewForm = (): ReactElement => {
  const WithIntegration = withDataLoaded($integrationContent, QueryActionForm, {
    propKey: "integration",
  });
  return <WithIntegration />;
};

const QueryActionEditForm = (): ReactElement => {
  const WithIntegration = withDataLoaded($integrationContent, QueryActionForm, {
    propKey: "integration",
  });
  const WithQueryAction = withDataLoaded($queryActionContent, WithIntegration, {
    propKey: "queryAction",
  });
  return <WithQueryAction />;
};

const EntityClassEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($entityClass, EntityClassFormPage, {
    propKey: "entityClass",
  });
  return <Wrapped />;
};

const AlertSourceEdit = (): ReactElement => {
  const Wrapped = withDataLoaded($alertSourceContent, AlertSourceForm, {
    propKey: "alertSource",
  });
  return <Wrapped />;
};

const InvokeAgentFormWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($alertSourceContent, InvokeAgent, {
    propKey: "alertSource",
  });
  return <Wrapped />;
};

const DestinationEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($destinationContent, DestinationForm, {
    propKey: "destination",
  });
  return <Wrapped />;
};

const EntityNewForm = (): ReactElement => {
  const Wrapped = withDataLoaded($entityClass, EntityFormFullPage, {
    propKey: "entityClass",
  });
  return <Wrapped />;
};

const EntityEditForm = (): ReactElement => {
  const WithEntityClass = withDataLoaded($entityClass, EntityFormFullPage, {
    propKey: "entityClass",
  });
  const WithQueryAction = withDataLoaded($entityContent, WithEntityClass, {
    propKey: "entity",
  });
  return <WithQueryAction />;
};

const AlertDetailsFormWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($eventContent, AlertDetails, {
    propKey: "event",
    refreshInterval: 10000, // Poll every 10 seconds
    shouldStopPolling: (data) => {
      const terminalStatuses: WorkflowStatus[] = [
        WorkflowStatus.ABORTED,
        WorkflowStatus.FAILED,
        WorkflowStatus.SUCCEEDED,
        WorkflowStatus.TIMED_OUT,
      ];
      return terminalStatuses.includes(data.status);
    },
  });
  return <Wrapped />;
};

const KnowledgeBaseDetailWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($knowledgeBaseContent, KnowledgeBaseDetail, {
    propKey: "knowledgeBase",
  });
  return <Wrapped reload={() => $knowledgeBaseContent.fetch()} />;
};

const appConfig = getAppConfig();

export const Routes = (): null | ReactElement => {
  const page = useStore($router);
  const auth = useAuthenticator((context) => [context.authStatus]);

  useEffect(() => {
    const setupAuth = async (): Promise<void> => {
      if (auth.authStatus === "unauthenticated") {
        redirectPage($router, "signIn");
      }

      try {
        const authSession = await fetchAuthSession();
        const rawToken = authSession.tokens?.accessToken?.toString();

        if (rawToken) {
          client.setConfig({
            auth: rawToken,
            baseUrl: appConfig.backendUrl,
            throwOnError: true,
          });

          if (appConfig.envMode === "production") {
            client.interceptors.response.use(async (response) => {
              if (!response.ok) {
                // Clone response to preserve original body stream which can only be consumed once
                const clonedResponse = response.clone();
                const body = await clonedResponse.json();

                Sentry.captureException(
                  new Error(
                    `API Error: ${response.status} ${response.statusText}`,
                  ),
                  {
                    extra: {
                      status: response.status,
                      statusText: response.statusText,
                      url: response.url,
                      ...(body as Record<string, unknown>),
                    },
                    tags: {
                      source: "hey-api-client",
                    },
                  },
                );
              }

              return response;
            });
          }
        }
      } catch (error) {
        console.error("Error setting up authentication:", error);
      }
    };

    void setupAuth();
  }, [auth.authStatus]);

  if (page?.route === "signIn") {
    if (auth.authStatus === "authenticated") {
      redirectPage($router, "index");
    }

    if (auth.authStatus === "unauthenticated") {
      return <SignIn />;
    }
  }

  if (page?.route === "authCallback") {
    return (
      <StackedLayout navbar={null} sidebar={null}>
        <AuthCallback />
      </StackedLayout>
    );
  }

  if (auth.authStatus === "configuring") {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (page === undefined) {
    return (
      <RailLayout>
        <PageNotFound />
      </RailLayout>
    );
  }

  if (page.route === "index") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <HomePage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "chat") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Chat />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "dashboard") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Dashboard />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationsList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrationsCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationType />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "settings") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <SettingsPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "graph") {
    return (
      <GraphLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Graph />
        </Suspense>
      </GraphLayout>
    );
  }

  if (page.route === "newIntegrations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationForm integrationType={page.params.type} />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrationsEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entities") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntitiesPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityClassCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityClassFormPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityClassEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityClassEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityNewForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "queryActionNew" || page.route === "queryActionEdit") {
    if (page.route === "queryActionNew") {
      return (
        <RailLayout>
          <Suspense fallback={<LoadingIndicator />}>
            <QueryActionNewForm />
          </Suspense>
        </RailLayout>
      );
    }

    if (page.route === "queryActionEdit") {
      return (
        <RailLayout>
          <Suspense fallback={<LoadingIndicator />}>
            <QueryActionEditForm />
          </Suspense>
        </RailLayout>
      );
    }
  }

  if (page.route === "alerts") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Alerts />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "alertDetails") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AlertDetailsFormWithData />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "alertSources") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AlertSourcesList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "alertSourcesCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AlertSourceForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "alertSourcesEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AlertSourceEdit />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "knowledgeBase") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <KnowledgeBase />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "knowledgeBaseDetail") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <KnowledgeBaseDetailWithData />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Destinations />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinationsCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationType />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "newDestinations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationForm destinationType={page.params.type} />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinationsEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "agentInvoke") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <InvokeAgentFormWithData />
        </Suspense>
      </RailLayout>
    );
  }

  return null;
};
